import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// MUI Components
import {
    Box, Paper, Typography, Grid, Button, Select, MenuItem,
    FormControl, InputLabel, Divider, ToggleButton, ToggleButtonGroup,
    Checkbox, ListItemText, OutlinedInput, LinearProgress, Chip
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

// Services
import StakeholderService from "../services/StakeholderService";
import ActionService from "../services/ActionService";
import AziendaService from "../services/AziendeService";

// Custom Components
import { ExportToExcel } from '../ExportToExcel';
import HelpGuideDrawerReport from "./HelpGuideDrawerReport";
import ReportTable from './ReportTableAvgTime'; 
import ReportTableTimeSlots from "./ReportTableTimeSlots";
import ContractsByYearChart from "./ContractsByYearChart";
import ContractsByTypeChart from "./ContractsByTypeChart";

dayjs.extend(isBetween);
dayjs.extend(customParseFormat);

const SimpleReportCards = ({ data, reportType, baseDate, allSimpleActions }) => {
    const countUniqueSh = (d) => new Set(d.map(item => item.contatto_id)).size;
    const countUniqueShByActivity = (d, type) => new Set(d.filter(item => item.tipologia === type).map(item => item.contatto_id)).size;

    const sumTime = (d) => {
        let totalMinutes = 0;
        d.forEach(row => {
            if (row.orario_inizio && row.orario_fine) {
                const start = dayjs(`1970-01-01T${row.orario_inizio}`);
                const end = dayjs(`1970-01-01T${row.orario_fine}`);
                if (start.isValid() && end.isValid() && end.isAfter(start)) {
                    totalMinutes += end.diff(start, 'minute');
                }
            }
        });
        const h = Math.floor(totalMinutes / 60);
        const m = totalMinutes % 60;
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    };

    if (reportType === 1) {
        const dateRanges = {
            '30 GG': baseDate.subtract(30, 'day'), '60 GG': baseDate.subtract(60, 'day'), '90 GG': baseDate.subtract(90, 'day'),
        };
        return (
            <Grid container spacing={2}>
                {Object.entries(dateRanges).map(([label, startDate]) => {
                    const rangeData = allSimpleActions.filter(act =>
                        dayjs(act.data).isAfter(startDate) && dayjs(act.data).isBefore(baseDate.add(1, 'day'))
                    );
                    return (
                        <Grid item xs={12} sm={4} key={label}><Paper sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ bgcolor: 'primary.main', color: 'white', mb: 1 }}>{label}</Typography>
                            <Typography color="text.secondary">Numero totale attivi</Typography>
                            <Typography variant="h4">{countUniqueSh(rangeData)}</Typography>
                        </Paper></Grid>
                    );
                })}
            </Grid>
        );
    }
    if (reportType === 2) {
        const activityTypes = ["WOD", "JS", "WESE", "VOL", "IR", "RO"];
        return (
            <Grid container spacing={2}>
                {activityTypes.map(type => {
                    const typeData = data.filter(d => d.tipologia === type);
                    return (
                        <Grid item xs={12} sm={4} md={2} key={type}><Paper sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ bgcolor: 'primary.main', color: 'white', mb: 1 }}>{type}</Typography>
                            <Typography color="text.secondary">Partecipanti Unici</Typography>
                            <Typography variant="h5">{countUniqueShByActivity(data, type)}</Typography>
                            <Divider sx={{ my: 1 }} /><Typography color="text.secondary">Totale Ore</Typography>
                            <Typography variant="h5">{sumTime(typeData)}</Typography>
                        </Paper></Grid>
                    );
                })}
            </Grid>
        );
    }
    return null;
};

const allTipologie = [
    { sigla: 'IR', descrizione: 'Chiamate in entrata' }, { sigla: 'OR', descrizione: 'Chiamate in uscita' },
    { sigla: 'VOL', descrizione: 'Volontariato' }, { sigla: 'WESE', descrizione: 'Weekend/Serali' },
    { sigla: 'WOD', descrizione: 'Work Order Day' }, { sigla: 'JS', descrizione: 'Job Station' },
    { sigla: 'SPORT', descrizione: 'Attività Sportiva' }, { sigla: 'FORMAZIONE', descrizione: 'Corso di Formazione' },
    { sigla: 'TE', descrizione: 'Sperimentazioni Temporanee' }, { sigla: 'SE', descrizione: 'Lavoro Esterno Supportato' },
    { sigla: 'IE', descrizione: 'Lavoro Indipendente' }, { sigla: 'JS-TE', descrizione: 'Job Station - Sperimentazioni' },
    { sigla: 'JS-SE', descrizione: 'Job Station - Lavoro Esterno' }, { sigla: 'JS-IE', descrizione: 'Job Station - Lavoro Indipendente' }
];

const Report = (props) => {
    const navigate = useNavigate();
    const isAdmin = props.user?.roles?.includes("ROLE_ADMIN") || false;
    const userSede = props.user?.sede;

    const [actionsList, setActionsList] = useState([]);
    const [aziende, setAziende] = useState([]);
    const [allSedi, setAllSedi] = useState([]);

    const [query, setQuery] = useState({
        data_inizio: null,
        data_fine: dayjs().format('YYYY-MM-DD'),
        tipologia: [],
        sedi: []
    });

    const [viewMode, setViewMode] = useState('simple');
    const [selectedReport, setSelectedReport] = useState(0);
    const [loading, setLoading] = useState(true);

    const simpleTypes = useMemo(() => ['IR', 'OR', 'VOL', 'WESE', 'WOD', 'JS', 'SPORT'], []);

    useEffect(() => {
        StakeholderService.getAll()
            .then(res => {
                const sedi = res.data.map(sh => sh.sede).filter(Boolean);
                const uniqueSedi = [...new Set(sedi)].sort();
                setAllSedi(uniqueSedi);
                if (isAdmin) setQuery(prev => ({ ...prev, sedi: uniqueSedi }));
            })
            .catch(e => console.error("Error fetching sedi:", e));
        AziendaService.getAll().then(res => setAziende(res.data));
    }, [isAdmin]);

    useEffect(() => {
        const fetchData = () => {
            setLoading(true);
            const isHistorical = viewMode === 'complex' && (selectedReport === 1 || selectedReport === 2);
            const params = {
                data_inizio: isHistorical ? null : query.data_inizio,
                data_fine: isHistorical ? null : query.data_fine,
                tipologia: query.tipologia.length > 0 ? query.tipologia : null,
            };
            if (isAdmin) {
                if (query.sedi.length > 0) params.sede = query.sedi;
            } else {
                params.sede = userSede;
            }
            ActionService.getAll(params)
                .then(res => setActionsList(res.data))
                .catch(e => console.error("Error fetching actions:", e))
                .finally(() => setLoading(false));
        };
        const timerId = setTimeout(fetchData, 500);
        return () => clearTimeout(timerId);
    }, [query, isAdmin, userSede, viewMode, selectedReport]);

    const { dataToShow, unfilteredSimpleActions, unfilteredComplexActions } = useMemo(() => {
        if (!actionsList) return { dataToShow: [], unfilteredSimpleActions: [], unfilteredComplexActions: [] };

        const filtered = actionsList.filter(action => {
            const matchesTipologia = query.tipologia.length === 0 || query.tipologia.includes(action.tipologia);
            const actionSede = action.stakeholders?.sede;
            const matchesSede = isAdmin 
                ? (query.sedi.length === 0 || query.sedi.includes(actionSede))
                : actionSede === userSede;
            
            const actionDate = dayjs(action.data || action.data_inizio);
            const isHistorical = viewMode === 'complex' && (selectedReport === 1 || selectedReport === 2);
            let matchesDate = true;
            if(!isHistorical) {
                if (query.data_inizio && actionDate.isBefore(dayjs(query.data_inizio), 'day')) matchesDate = false;
                if (query.data_fine && actionDate.isAfter(dayjs(query.data_fine), 'day')) matchesDate = false;
            }
            return matchesTipologia && matchesSede && matchesDate;
        });

        const simple = [], complex = [], formazione = [];
        filtered.forEach(action => {
            if (action.stakeholders) {
                if (simpleTypes.includes(action.tipologia)) simple.push(action);
                else if (action.tipologia === 'FORMAZIONE') formazione.push(action);
                else complex.push(action);
            }
        });

        const selectedData = viewMode === 'simple' ? simple : (viewMode === 'formazione' ? formazione : complex);
        return { dataToShow: selectedData, unfilteredSimpleActions: simple, unfilteredComplexActions: complex };
    }, [actionsList, viewMode, simpleTypes, query, isAdmin, userSede, selectedReport]);

    // COLONNE
    const dateFormatter = (params) => params.value ? dayjs(params.value).format('DD/MM/YYYY') : '';

    const simpleColumns = [
        { field: 'nome', headerName: 'Nome', width: 120, valueGetter: p => p.row.stakeholders?.nome || 'N/D' },
        { field: 'cognome', headerName: 'Cognome', width: 120, valueGetter: p => p.row.stakeholders?.cognome || 'N/D' },
        { field: 'data', headerName: 'Data', width: 110, valueFormatter: dateFormatter },
        { field: 'tipologia', headerName: 'Tipologia', width: 120 },
        { field: 'orario_inizio', headerName: 'Inizio', width: 90 },
        { field: 'orario_fine', headerName: 'Fine', width: 90 },
    ];

    const complexColumns = [
        { field: 'nome', headerName: 'Nome', width: 120, valueGetter: p => p.row.stakeholders?.nome || 'N/D' },
        { field: 'cognome', headerName: 'Cognome', width: 120, valueGetter: p => p.row.stakeholders?.cognome || 'N/D' },
        { field: 'tipologia', headerName: 'Tipo', width: 110 },
        { field: 'azienda_id', headerName: 'Azienda', width: 180, valueGetter: (p) => aziende.find(a => a.id === p.row.azienda_id)?.ragione_sociale || 'N/D' },
        { field: 'data_inizio', headerName: 'Data Inizio', width: 110, valueFormatter: dateFormatter },
        { field: 'esito', headerName: 'Esito', width: 120 },
    ];

    const formazioneColumns = [
        { field: 'nome', headerName: 'Nome', width: 120, valueGetter: p => p.row.stakeholders?.nome || 'N/D' },
        { field: 'cognome', headerName: 'Cognome', width: 120, valueGetter: p => p.row.stakeholders?.cognome || 'N/D' },
        { field: 'azienda_id', headerName: 'Ente Cert.', width: 180, valueGetter: (p) => aziende.find(a => a.id === p.row.azienda_id)?.ragione_sociale || 'N/D' },
    ];

    const getCurrentColumns = () => {
        if (viewMode === 'simple') return simpleColumns;
        if (viewMode === 'formazione') return formazioneColumns;
        return complexColumns;
    };

    // CALCOLI REPORT E GRAFICI
    const calculateNewContractsByYear = useMemo(() => {
        const res = {};
        unfilteredComplexActions.forEach(a => {
            const year = dayjs(a.data_inizio).year();
            if (year) res[year] = (res[year] || 0) + 1;
        });
        return Object.entries(res).map(([year, count], id) => ({ id, year, count })).sort((a, b) => b.year - a.year);
    }, [unfilteredComplexActions]);

    // FIX: Logica Grafico per Tipo
    const calculateContractsByType = useMemo(() => {
        const res = {};
        dataToShow.forEach(a => {
            const tipo = a.tipologia || 'Non specificato';
            res[tipo] = (res[tipo] || 0) + 1;
        });
        return Object.entries(res).map(([type, count]) => ({ 
            id: type, 
            type: type, // Assicurati che ContractsByTypeChart legga la chiave 'type'
            count: count 
        }));
    }, [dataToShow]);

    const calculateContractsByCompanyYear = useMemo(() => {
        const res = {};
        unfilteredComplexActions.forEach(a => {
            const y = dayjs(a.data_inizio).year();
            const az = aziende.find(az => az.id === a.azienda_id)?.ragione_sociale || 'N/D';
            if (!res[az]) res[az] = { company: az };
            res[az][y] = (res[az][y] || 0) + 1;
        });
        return Object.values(res).map((v, i) => ({ id: i, ...v }));
    }, [unfilteredComplexActions, aziende]);

    const handleReset = () => {
        setQuery({ data_inizio: null, data_fine: dayjs().format('YYYY-MM-DD'), tipologia: [], sedi: isAdmin ? allSedi : [] });
        setSelectedReport(0);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ flexGrow: 1, p: 2 }}>
                <Paper sx={{ p: 2, mb: 2 }}>
                    <HelpGuideDrawerReport />
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>Filtri Report</Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <ToggleButtonGroup color="primary" value={viewMode} exclusive onChange={(e, v) => { if(v) { setViewMode(v); handleReset(); }}}>
                            <ToggleButton value="simple">Attività Club</ToggleButton>
                            <ToggleButton value="complex">Attività Lavorative</ToggleButton>
                            <ToggleButton value="formazione">Formazione</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <Grid container spacing={2} alignItems="center">
                        <Grid item>
                            <DatePicker label="Da" format="DD/MM/YYYY" value={query.data_inizio ? dayjs(query.data_inizio) : null} onChange={v => setQuery(p => ({...p, data_inizio: v?.format('YYYY-MM-DD')}))} />
                        </Grid>
                        <Grid item>
                            <DatePicker label="A" format="DD/MM/YYYY" value={query.data_fine ? dayjs(query.data_fine) : null} onChange={v => setQuery(p => ({...p, data_fine: v?.format('YYYY-MM-DD')}))} />
                        </Grid>
                        
                        <Grid item>
                            <FormControl sx={{ minWidth: 220 }}>
                                <InputLabel>Tipologie</InputLabel>
                                <Select multiple value={query.tipologia} onChange={e => setQuery(p => ({...p, tipologia: e.target.value}))} input={<OutlinedInput label="Tipologie" />} renderValue={s => <Box sx={{display:'flex', flexWrap:'wrap', gap:0.5}}>{s.map(v => <Chip key={v} label={v} size="small" />)}</Box>}>
                                    {allTipologie.filter(t => (viewMode === 'simple' && simpleTypes.includes(t.sigla)) || (viewMode === 'complex' && !simpleTypes.includes(t.sigla) && t.sigla !== 'FORMAZIONE') || (viewMode === 'formazione' && t.sigla === 'FORMAZIONE')).map(t => (
                                        <MenuItem key={t.sigla} value={t.sigla}><Checkbox checked={query.tipologia.indexOf(t.sigla) > -1} /><ListItemText primary={t.sigla} /></MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {isAdmin && (
                            <Grid item>
                                <FormControl sx={{ minWidth: 220 }}>
                                    <InputLabel>Sede</InputLabel>
                                    <Select multiple value={query.sedi} onChange={e => setQuery(p => ({...p, sedi: e.target.value}))} input={<OutlinedInput label="Sede" />} renderValue={s => s.length === allSedi.length ? "Tutte" : s.join(', ')}>
                                        {allSedi.map(s => (
                                            <MenuItem key={s} value={s}><Checkbox checked={query.sedi.indexOf(s) > -1} /><ListItemText primary={s} /></MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        <Grid item>
                            <FormControl sx={{ minWidth: 220 }}>
                                <InputLabel>Seleziona Report</InputLabel>
                                <Select value={selectedReport} label="Seleziona Report" onChange={e => setSelectedReport(e.target.value)}>
                                    <MenuItem value={0}>Dati grezzi</MenuItem>
                                    {viewMode === 'simple' ? [
                                        <MenuItem key="s1" value={1}>Iscritti/Attivi</MenuItem>,
                                        <MenuItem key="s2" value={2}>Attività</MenuItem>,
                                        <MenuItem key="s3" value={3}>Media Ore</MenuItem>,
                                        <MenuItem key="s4" value={4}>Fasce Orarie</MenuItem>
                                    ] : viewMode === 'complex' ? [
                                        <MenuItem key="c1" value={1}>Per Azienda/Anno</MenuItem>,
                                        <MenuItem key="c2" value={2}>Nuovi Contratti</MenuItem>,
                                        <MenuItem key="c3" value={3}>Per Tipo</MenuItem>
                                    ] : <MenuItem value={1}>Statistiche Formazione</MenuItem>}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item>
                            <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={handleReset}>Reset</Button>
                        </Grid>
                    </Grid>
                </Paper>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={9}>
                        {selectedReport !== 0 && (
                            <Paper sx={{ p: 2, mb: 2 }}>
                                {viewMode === 'simple' && <SimpleReportCards data={dataToShow} reportType={selectedReport} baseDate={dayjs(query.data_fine)} allSimpleActions={unfilteredSimpleActions} />}
                                {viewMode === 'simple' && selectedReport === 3 && <ReportTable data={dataToShow} startDate={query.data_inizio} endDate={query.data_fine} />}
                                {viewMode === 'simple' && selectedReport === 4 && <ReportTableTimeSlots data={dataToShow} startDate={query.data_inizio} endDate={query.data_fine} />}
                                {viewMode === 'complex' && selectedReport === 1 && <DataGrid rows={calculateContractsByCompanyYear} columns={[{ field: 'company', headerName: 'Azienda', width: 200 }, ...Object.keys(calculateContractsByCompanyYear[0] || {}).filter(k => !['id','company'].includes(k)).map(y => ({ field: y, headerName: y, width: 80 }))]} autoHeight />}
                                {viewMode === 'complex' && selectedReport === 2 && <DataGrid rows={calculateNewContractsByYear} columns={[{field:'year', headerName:'Anno', width:150}, {field:'count', headerName:'Contratti', width:150}]} autoHeight />}
                                {viewMode === 'complex' && selectedReport === 3 && <DataGrid rows={calculateContractsByType} columns={[{field:'type', headerName:'Tipo', flex:1}, {field:'count', headerName:'Numero', width:120}]} autoHeight />}
                            </Paper>
                        )}
                        <Paper sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="h6">Dettaglio Record ({dataToShow.length})</Typography>
                                <ExportToExcel apiData={dataToShow.map(d => ({ ...d, nome: d.stakeholders?.nome, cognome: d.stakeholders?.cognome, sede: d.stakeholders?.sede }))} fileName={`Report_${viewMode}`} />
                            </Box>
                            <Box sx={{ height: 600 }}>
                                {loading && <LinearProgress sx={{mb:1}} />}
                                <DataGrid rows={dataToShow} columns={getCurrentColumns()} getRowId={r => r.id || Math.random()} onRowDoubleClick={e => navigate(`/stakeholders/${e.row.contatto_id}/azione`)} />
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>Grafici</Typography>
                            {viewMode === 'complex' && selectedReport === 2 && <ContractsByYearChart data={calculateNewContractsByYear} />}
                            {viewMode === 'complex' && selectedReport === 3 && <ContractsByTypeChart data={calculateContractsByType} />}
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </LocalizationProvider>
    );
};

export default Report;